/**
 * BotBatchPage — Virtual user batch creation tool (§2.21 add1).
 *
 * Two tabs:
 *   1. Create form (count, prefix, referrer, initial balances)
 *   2. History (recently batch-created bots)
 */
import { useState } from 'react';
import { Button, Form, Input, InputNumber, Select, Space, Tabs, Table, Tag, App, Card, Alert, Descriptions } from 'antd';
import { RobotOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { TOKEN_IDS, TOKEN_META, type TokenId } from '@uo/shared';

export default function BotBatchPage() {
  const { message: msg } = App.useApp();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('bot_flag.batch_create');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<{ count: number; users: { id: number; username: string; invite_code: string }[] } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Optional: which tokens to give initial balance
  const [selectedTokens, setSelectedTokens] = useState<string[]>([]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const body: any = {
        count: values.count,
        prefix: values.prefix || 'bot',
        bot_note: values.bot_note || 'batch created',
      };
      if (values.referrer_id) body.referrer_id = values.referrer_id;

      // Build initial balances
      const balances: Record<string, string> = {};
      for (const tok of selectedTokens) {
        const amt = values[`init_${tok}`];
        if (amt && parseFloat(amt) > 0) {
          balances[tok] = String(amt);
        }
      }
      if (Object.keys(balances).length > 0) {
        body.initial_balances = balances;
      }

      setSubmitting(true);
      const res: any = await api.post('/api/admin/bot-batch/create', body);
      setLastResult(res.data);
      msg.success(`成功创建 ${res.data.count} 个虚拟用户`);
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      msg.error(e?.response?.data?.msg || e.message || '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const historyColumns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '用户名', dataIndex: 'username', width: 160 },
    { title: '邀请码', dataIndex: 'invite_code', width: 100 },
    { title: '推荐人', dataIndex: 'referrer_id', width: 80, render: (v: number | null) => v ?? '-' },
    { title: '备注', dataIndex: 'bot_note', width: 200, ellipsis: true },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (v: string) => <Tag color={v === 'active' ? 'green' : 'red'}>{v}</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
  ];

  return (
    <Tabs
      defaultActiveKey="create"
      items={[
        {
          key: 'create',
          label: '批量创建',
          children: canCreate ? (
            <div style={{ maxWidth: 600 }}>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="批量创建虚拟用户用于做市。每个用户自动标记 is_bot=true，可选设置初始余额。"
              />
              <Form form={form} layout="vertical">
                <Form.Item name="count" label="创建数量" rules={[{ required: true }]} initialValue={10}>
                  <InputNumber min={1} max={500} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="prefix" label="用户名前缀" initialValue="bot">
                  <Input placeholder="bot" maxLength={10} />
                </Form.Item>
                <Form.Item name="referrer_id" label="推荐人ID（可选）">
                  <InputNumber min={1} style={{ width: '100%' }} placeholder="所有bot共享同一推荐人" />
                </Form.Item>
                <Form.Item name="bot_note" label="备注" initialValue="market making batch">
                  <Input.TextArea rows={2} maxLength={255} />
                </Form.Item>

                {/* Token initial balances */}
                <Form.Item label="初始余额（可选）">
                  <Select
                    mode="multiple"
                    placeholder="选择要充值的币种"
                    value={selectedTokens}
                    onChange={setSelectedTokens}
                    options={TOKEN_IDS.map((t: TokenId) => ({
                      value: t,
                      label: `${TOKEN_META[t].symbol} — ${TOKEN_META[t].name}`,
                    }))}
                    style={{ width: '100%', marginBottom: 8 }}
                  />
                  {selectedTokens.map((tok) => (
                    <Form.Item key={tok} name={`init_${tok}`} label={`${(TOKEN_META as any)[tok]?.symbol ?? tok} 数量`} style={{ marginBottom: 8 }}>
                      <Input placeholder="0" />
                    </Form.Item>
                  ))}
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                    loading={submitting}
                  >
                    批量创建
                  </Button>
                </Form.Item>
              </Form>

              {/* Last result */}
              {lastResult && (
                <Card title={`最近创建: ${lastResult.count} 个`} size="small" style={{ marginTop: 16 }}>
                  <Table
                    dataSource={lastResult.users}
                    columns={[
                      { title: 'ID', dataIndex: 'id', width: 80 },
                      { title: '用户名', dataIndex: 'username', width: 180 },
                      { title: '邀请码', dataIndex: 'invite_code', width: 100 },
                    ]}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ y: 300 }}
                  />
                </Card>
              )}
            </div>
          ) : (
            <p style={{ color: '#999' }}>无批量创建权限</p>
          ),
        },
        {
          key: 'history',
          label: '创建记录',
          children: (
            <DataTable<{ id: number; username: string; invite_code: string; referrer_id: number | null; bot_note: string | null; status: string; create_time: string }>
              queryKey={['bot-batch-history', refreshKey]}
              endpoint="/api/admin/bot-batch/history"
              columns={historyColumns}
              rowKey="id"
            />
          ),
        },
      ]}
    />
  );
}
